import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { APP_BASE } from '@/config/basePath'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft, Loader2, Save, Undo2, Redo2,
  StickyNote, FileText, Settings, Sliders, Plus, Eye, MoreVertical,
  Maximize2, Minimize2, PanelLeftClose, PanelRightClose, Columns3,
} from 'lucide-react'
import type { EmbeddedDesignerProps } from './embeddedDesigner'
import { loadPageObject, savePageObject, generateBusinessLogicFile } from '@/api/designers'
import { getJsonRaw } from '@/api/generateapp'
import { useProjectStore } from '@/store/projectStore'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { useAgentRefresh } from '@/hooks/useAgentRefresh'
import DiscardChangesModal from '@/components/common/DiscardChangesModal'
import type { RawAppData } from '@/types'
import type { Block, BlockType, LayoutMode, PageAction, PageKind, PageModel, PageRoot, Section, Selection, Viewport } from './page/types'
import { makeBlock, newId } from './page/blocks'
import BlockNode from './page/BlockNode'
import PreviewModal from './page/PreviewModal'
import { toV2, ensureMainSection, newSectionId, normalizePageKind } from './page/migrate'
import { applyAutoEditableToPage } from './page/PropertyPanel'
import { AdvancedPanel } from './page/AdvancedPanel'
import { DeviceToggle, viewportWidth } from './page/DeviceToggle'
import { SectionStrip } from './page/SectionStrip'
import { InheritedChromeBand } from './page/InheritedChromeBand'
import { SiteMenuContext, SiteMenusContext, type SiteMenuItem, type SiteMenus } from './page/renderers'
import { ViewportContext } from './page/ViewportContext'
import { buildSiteMenu, buildSiteMenus, siteNodeIdFor } from './page/siteMenu'
import { AddSectionButton } from './page/AddSectionButton'
import { usePageApiVariables } from './page/pageApiVariables'
import {
  updateBlockInSection, insertBlockInSectionAt, deleteBlockInSection, findMainSectionId,
  findBlockLocation, moveBlockToSectionAt, nextBlockIdForType,
  walkBlocks, mapBlockSlots,
} from './page/pageOps'
import { NestedBlockContext } from './page/ChildBlockList'
import {
  resolveTheme, themeCssVars, backgroundCss, chromeBackgroundCss,
  CONTENT_WIDTH_PX, FONT_STACKS, PARALLAX_CSS, pageParallaxStyle, pageRuntimeCss,
} from './page/theme'
import SlashMenu from './page/SlashMenu'
import PropertyPanel from './page/PropertyPanel'
import { PageVarsContext } from './page/renderers'
import {
  PageRuntimeContext, PageFieldErrorsContext, PageFieldRefsContext,
  type ActionState, type FieldErrors, type PageRuntime,
} from './page/actions/runtime'
import { runAction, buildResolver, type ActionCtx } from './page/actions/runner'
import { getPath, toDisplayString } from './page/actions/substitute'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'

const MAX_UNDO = 50
const SMART_PAGE_LAYOUT_KEY = 'twasta.smartPage.layout'

// Display label for a block type in the drag preview. Falls back to a
// title-cased version of the type id so new block types render acceptably.
function humanBlockLabel(type: string): string {
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function getDefaultPage(name: string, kind: PageKind = 'internal'): PageModel {
  // Title intentionally left blank — the user opts in to a title by typing one
  // in Page Properties (or by adding a Heading block with {{title}}).
  const seed: PageModel = {
    obj_name: name,
    title: '',
    description: '',
    inputs: [],
    blocks: [makeBlock('paragraph', 'paragraph_1')],
    kind,
  }
  // For Smart Document templates, every applicable block starts editable
  // — saves the author from manually flipping each one. (Old behavior
  // prompted on toggle into document mode; with kind immutable, we run
  // it once at creation.)
  return kind === 'document' ? applyAutoEditableToPage(seed) : seed
}

interface PageDesignerExtraProps {
  // Embedded mode only: route an "Open in code editor" request (from the
  // before-load access event) to the object's own closeable Code tab in the
  // workbench, with the resolved file preopened — exactly like the
  // transaction designer.
  onOpenCodeFile?: (relPath: string | null) => void
}

export default function PageDesignerPage({
  embedded = false,
  hostFullscreen = false,
  embedProject,
  embedObjName,
  onDirtyChange,
  registerSave,
  onOpenCodeFile,
}: EmbeddedDesignerProps & PageDesignerExtraProps = {}) {
  const params = useParams<{ project: string; objName: string }>()
  const project = embedProject ?? params.project
  useRunOnlyRedirect(project)
  const objName = embedObjName ?? params.objName
  const navigate = useNavigate()
  const location = useLocation()
  const projectName = project || ''
  const objectName = objName || ''
  const { currentProject, selectProject } = useProjectStore()

  useEffect(() => {
    if (project && currentProject?.application.folder_name !== project) {
      selectProject(project)
    }
  }, [project, currentProject, selectProject])

  // Hint passed by the workbench when opening a freshly-created node.
  // Only used to seed brand-new pages — for existing pages, the kind
  // stored in the JSON file always wins.
  const seedKind = ((location.state as { page_kind?: PageKind } | null)?.page_kind ?? 'internal') as PageKind
  const [page, setPage] = useState<PageModel>(getDefaultPage(objectName, seedKind))
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { canEditObjectsOrLoading: canEdit } = useProjectPermissions()
  // Default layout hides the left Outline panel — most authoring time is
  // spent on the center canvas with the right Property panel. Authors who
  // want the outline back can switch via the toolbar's layout kebab; the
  // choice is persisted in localStorage so it sticks across sessions.
  const [layout, setLayout] = useState<LayoutMode>(() => {
    try {
      const stored = localStorage.getItem(SMART_PAGE_LAYOUT_KEY)
      if (stored === 'all' || stored === 'center-only' || stored === 'left-center' || stored === 'center-right') {
        return stored
      }
    } catch { /* localStorage disabled / private mode — fall through */ }
    return 'center-right'
  })
  useEffect(() => {
    try { localStorage.setItem(SMART_PAGE_LAYOUT_KEY, layout) } catch { /* ignore */ }
  }, [layout])
  const [selection, setSelection] = useState<Selection>({ type: 'page' })
  const [transactions, setTransactions] = useState<{ obj_name: string; title: string }[]>([])
  const [siteMenu, setSiteMenu] = useState<SiteMenuItem[]>([])
  // The site's NAMED menus (resolved — the canvas renders them, the property
  // panel offers them in the nav_menu Menu picker) and the website node id
  // (for the "website settings" links).
  const [siteMenus, setSiteMenus] = useState<SiteMenus>({})
  const [siteNodeId, setSiteNodeId] = useState('')
  // Bumped when a page's menu membership changes so the site menus re-load.
  const [siteMenusEpoch, setSiteMenusEpoch] = useState(0)
  // True when the page being edited is itself a site header/footer chrome
  // page (named as header_page_obj_name / footer_page_obj_name on an X site
  // node). Chrome pages render their background from `palette.background`
  // (via chromeBackgroundCss) so a dark header/footer isn't clobbered white
  // by the preset's default light `layout.background` — matching the SSR
  // ChromePage + in-app renderChrome output.
  const [isChromePage, setIsChromePage] = useState(false)

  // Working values for inputs in the canvas (for live filter preview)
  // Variables can hold arbitrary JSON values (objects/arrays) when the
  // PageInput has type:'json'. Other types still hold strings as before.
  const [inputValues, setInputValues] = useState<Record<string, unknown>>({})

  // Slash menu
  // `mode='inline'` = opened by typing '/' inside a text block (query is read from contenteditable)
  // `mode='transform'` = opened via the transform button (query is internal to SlashMenu)
  const [slashMenu, setSlashMenu] = useState<{
    blockId: string
    mode: 'inline' | 'transform'
    query: string
    anchorLeft: number
    anchorTop: number
    anchorBottom: number
  } | null>(null)

  // Drag state
  const [draggedBlockId, setDraggedBlockId] = useState<string | null>(null)
  const [dropTarget, setDropTarget] = useState<{ id: string; pos: 'before' | 'after' } | null>(null)
  // Mirror of dropTarget so the section-level fallback drop handler sees the
  // latest hovered indicator even when the mouse is released in dead space
  // (section padding, "Add block" area) where no per-block drop handler runs.
  const dropTargetRef = useRef(dropTarget)
  dropTargetRef.current = dropTarget
  // Tracks whether the in-progress drag has been resolved (drop succeeded or
  // explicitly toasted) — read by handleDragEnd to surface a reason when the
  // browser ended the drag without a successful drop on a target.
  const dragResolvedRef = useRef<boolean>(true)

  // Undo/redo
  const pageRef = useRef<PageModel>(page)
  pageRef.current = page
  const undoStackRef = useRef<string[]>([])
  const redoStackRef = useRef<string[]>([])
  const [undoCount, setUndoCount] = useState(0)
  const [redoCount, setRedoCount] = useState(0)

  // Snapshot of last-saved page for dirty detection. Set after load and
  // refreshed on each successful save.
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null)
  const isDirty = savedSnapshot !== null && JSON.stringify(page) !== savedSnapshot
  // Full-screen toggle for the embedded ("Design" tab) usage only.
  const [embedFullscreen, setEmbedFullscreen] = useState(false)
  const { guard, allowNextUnload, showDiscardModal, confirmDiscard, cancelDiscard } =
    useUnsavedChangesGuard(isDirty)

  // Reload the page model when an AI agent run touches project files so the
  // open designer reflects the agent's edits without a hard browser refresh.
  // Deferred while dirty so in-progress edits are never clobbered.
  const agentRefresh = useAgentRefresh(projectName, isDirty)

  // Ref on the center canvas scroll container — captured before
  // navigating into the transaction designer so we can put the user
  // back at the same scroll position when they return.
  const canvasScrollRef = useRef<HTMLDivElement | null>(null)

  // Panel widths — keep side panels compact so the canvas in the middle
  // gets the most room (the canvas also needs ~80px of horizontal gutter
  // inside it for the hover handles + delete button to land outside the
  // block; see canvas padding override below).
  const [leftWidth, setLeftWidth] = useState(220)
  const [rightWidth, setRightWidth] = useState(280)

  // Load page model
  useEffect(() => {
    const load = async () => {
      if (!projectName || !objectName) return
      setLoading(true)
      // A (re)load replaces the page wholesale (object switch or agent
      // refresh) — stale undo entries would restore a different/older page.
      undoStackRef.current = []
      redoStackRef.current = []
      setUndoCount(0)
      setRedoCount(0)
      try {
        const data = await loadPageObject(projectName, objectName)
        let nextPage: PageModel
        if (data.content) {
          const root = data.content as PageRoot
          const loaded: PageModel = root.page || (data.content as PageModel)
          // Defensive: ensure required arrays exist; migrate v1 → v2 in memory.
          // Spread the on-disk page FIRST so every key survives the load —
          // this used to be a fixed whitelist, and anything missing from it
          // (dataSources, chrome, …) vanished from the designer and was then
          // erased from disk by the next Save, which writes the whole file.
          nextPage = toV2({
            ...loaded,
            obj_name: loaded.obj_name || objectName,
            title: loaded.title || '',
            description: loaded.description || '',
            inputs: Array.isArray(loaded.inputs) ? loaded.inputs : [],
            blocks: Array.isArray(loaded.blocks) && loaded.blocks.length > 0
              ? loaded.blocks
              : [makeBlock('paragraph', 'paragraph_1')],
            actions: Array.isArray(loaded.actions) ? loaded.actions : undefined,
            theme: loaded.theme,
            pageSchemaVersion: loaded.pageSchemaVersion,
            sections: loaded.sections,
            designTokens: loaded.designTokens,
            seo: loaded.seo,
            variants: loaded.variants,
            i18n: loaded.i18n,
            navigation: loaded.navigation,
            // For loaded pages, the disk value of `kind` (and legacy
            // `pageMode`) wins; toV2 → normalizePageKind backfills.
            kind: loaded.kind,
            pageMode: loaded.pageMode,
            documentDefaults: loaded.documentDefaults,
            // Before-load access event — preserve the on-disk binding so the
            // Page panel's "Before Load / Access" section reflects it (without
            // this it was silently dropped on load and showed as undefined).
            beforeLoad: loaded.beforeLoad,
            runtimeAddable: loaded.runtimeAddable,
            runtimeAddableTypes: loaded.runtimeAddableTypes,
            runtimeDeletable: loaded.runtimeDeletable,
          })
        } else {
          // Fresh page — seed with the kind hint from the workbench.
          nextPage = toV2(getDefaultPage(objectName, seedKind))
        }
        setPage(nextPage)
        setSavedSnapshot(JSON.stringify(nextPage))
      } catch (err) {
        console.error('Failed to load page:', err)
        toast.error('Failed to load page')
        const fallback = toV2(getDefaultPage(objectName, seedKind))
        setPage(fallback)
        setSavedSnapshot(JSON.stringify(fallback))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [projectName, objectName, seedKind, agentRefresh])

  // When the user returns from another designer (e.g. transaction
  // designer reached via "Edit transaction" on an embed block), restore
  // the exact selection they had before leaving. We only attempt the
  // restore once: clearing history state on the way out prevents a
  // refresh from re-triggering it. The lookup must run AFTER the page
  // has loaded — `findBlockById` resolves nothing on the placeholder
  // empty page.
  const selectionRestoreTriedRef = useRef(false)
  useEffect(() => {
    if (loading || selectionRestoreTriedRef.current) return
    const state = location.state as {
      smartPageSelection?: Selection
      smartPageScrollTop?: number
    } | null
    const stashed = state?.smartPageSelection
    const scrollTop = state?.smartPageScrollTop
    selectionRestoreTriedRef.current = true
    if (stashed) {
      // For a block selection, verify the block still exists before
      // applying — otherwise we'd land on a "Block not found" empty
      // state.
      if (stashed.type === 'block' && stashed.blockId) {
        const loc = findBlockLocation(pageRef.current, stashed.blockId)
        if (loc) {
          setSelection({ type: 'block', blockId: stashed.blockId, sectionId: stashed.sectionId ?? loc.sectionId })
        }
      } else {
        setSelection(stashed)
      }
    }
    // Reapply the canvas scroll once the DOM has had a frame to lay out
    // — the canvas div is mounted by the time `loading` flips, but the
    // freshly-rendered sections need a tick to size.
    if (typeof scrollTop === 'number' && scrollTop > 0) {
      requestAnimationFrame(() => {
        if (canvasScrollRef.current) canvasScrollRef.current.scrollTop = scrollTop
      })
    }
    // Strip the restore payload from history so a browser refresh doesn't
    // keep re-applying it (mirrors PrototypeTab's pattern).
    try {
      const next = { ...(window.history.state || {}) }
      if (next.usr && typeof next.usr === 'object') {
        const { smartPageSelection: _drop1, smartPageScrollTop: _drop2, ...rest } = next.usr
        next.usr = rest
      }
      window.history.replaceState(next, '')
    } catch { /* ignore */ }
  }, [loading, location.state])

  // Load transactions list (for embed picker) AND this page's description/specs
  // from the application.json navigation entry.
  useEffect(() => {
    if (!projectName) return
    getJsonRaw(projectName)
      .then((res: RawAppData) => {
        const nav = res.navigation || []
        const txns = nav
          .filter((n) => n.obj_type === 'T' && n.obj_name)
          .map((n) => ({ obj_name: n.obj_name, title: n.title }))
        setTransactions(txns)
        // Build the site menu for this page's website so a nav_menu block
        // (e.g. when editing the site header) previews the real navigation.
        setSiteMenu(buildSiteMenu(nav as Parameters<typeof buildSiteMenu>[0], objectName))
        setSiteMenus(buildSiteMenus(nav as Parameters<typeof buildSiteMenu>[0], objectName))
        setSiteNodeId(siteNodeIdFor(nav as Parameters<typeof buildSiteMenu>[0], objectName))
        // Is THIS page a site chrome (header/footer) page? Scan every X site
        // node's spec for a header/footer reference to objectName.
        const isChrome = nav.some((n) => {
          if (n.obj_type !== 'X' || !n.functional_specs) return false
          try {
            const spec = JSON.parse(n.functional_specs) as Record<string, unknown>
            return spec.header_page_obj_name === objectName || spec.footer_page_obj_name === objectName
          } catch {
            return false
          }
        })
        setIsChromePage(isChrome)
      })
      .catch(() => { setTransactions([]); setSiteMenu([]); setSiteMenus({}); setIsChromePage(false) })
  }, [projectName, objectName, siteMenusEpoch])

  // Initialize input values from defaults whenever inputs change.
  // JSON-typed variables parse their stored default-as-string into the
  // real object/array so binding consumers see structured data.
  useEffect(() => {
    setInputValues((prev) => {
      const next: Record<string, unknown> = {}
      for (const inp of page.inputs) {
        if (prev[inp.name] !== undefined) {
          next[inp.name] = prev[inp.name]
          continue
        }
        if (inp.type === 'json') {
          if (inp.default) {
            try {
              next[inp.name] = JSON.parse(inp.default)
            } catch {
              next[inp.name] = undefined
            }
          } else {
            next[inp.name] = undefined
          }
        } else {
          next[inp.name] = inp.default || ''
        }
      }
      return next
    })
  }, [page.inputs])

  // Designer preview API engine — fires `autoload` requests and
  // `triggerOn` cascades so JSON variables populate inside the designer
  // without having to flip to the Angular runtime iframe. Errors are
  // swallowed quietly here; the Property Panel can surface them later
  // by passing onError hooks.
  usePageApiVariables(
    page.inputs,
    inputValues,
    useCallback((name: string, value: unknown) => {
      setInputValues((prev) => ({ ...prev, [name]: value }))
    }, []),
    undefined,
    useCallback((apiName: string) =>
      `${APP_BASE}/api/integration/invoke/${encodeURIComponent(projectName)}/${encodeURIComponent(apiName)}`,
    [projectName]),
  )

  // Update with undo tracking. Every mutation goes through ensureMainSection
  // so the v2 sections array stays in lock-step with the canvas's flat
  // `blocks` array — Sections are the source of truth.
  const handlePageChange = useCallback((next: PageModel) => {
    const currentJson = JSON.stringify(pageRef.current)
    const stack = undoStackRef.current
    stack.push(currentJson)
    if (stack.length > MAX_UNDO) stack.splice(0, stack.length - MAX_UNDO)
    undoStackRef.current = stack
    redoStackRef.current = []
    setUndoCount(stack.length)
    setRedoCount(0)
    setPage(ensureMainSection(next))
  }, [])

  const handleUndo = useCallback(() => {
    const stack = undoStackRef.current
    if (stack.length === 0) return
    const prev = stack.pop()!
    redoStackRef.current.push(JSON.stringify(pageRef.current))
    setUndoCount(stack.length)
    setRedoCount(redoStackRef.current.length)
    setPage(JSON.parse(prev))
  }, [])

  const handleRedo = useCallback(() => {
    const stack = redoStackRef.current
    if (stack.length === 0) return
    const next = stack.pop()!
    undoStackRef.current.push(JSON.stringify(pageRef.current))
    setUndoCount(undoStackRef.current.length)
    setRedoCount(stack.length)
    setPage(JSON.parse(next))
  }, [])

  // Save
  const handleSave = useCallback(async (): Promise<boolean> => {
    setSaving(true)
    try {
      // Sections are the source of truth. The flat `blocks` field is a
      // transient editor mirror — strip it from the saved payload.
      const cur = ensureMainSection(pageRef.current)
      const { blocks: _legacyMirror, ...rest } = cur
      const root: PageRoot = { page: { ...rest, blocks: [] } as PageModel }
      await savePageObject(projectName, objectName, root)
      setSavedSnapshot(JSON.stringify(pageRef.current))
      toast.success('Page saved')
      return true
    } catch (err) {
      console.error('Save failed:', err)
      toast.error('Failed to save page')
      return false
    } finally {
      setSaving(false)
    }
  }, [projectName, objectName])

  // "Open in code editor" for the before-load access event — mirrors the
  // transaction designer. Persists the binding first (so the stub is generated
  // from the latest function name / spec / language), resolves the file, then
  // opens it: embedded → the object's own closeable Code tab; standalone → a
  // new browser tab on the Source editor with the file preselected.
  const handleOpenCode = useCallback(async (method?: string) => {
    if (!projectName || !objectName) return
    const m = (method || 'before_load').trim() || 'before_load'
    const ok = await handleSave()
    if (!ok) return
    let relPath: string | null = null
    try {
      const res = await generateBusinessLogicFile(projectName, objectName, m, 'S')
      relPath = res?.path || null
    } catch {
      toast('Could not locate the code file; opening the Source editor.')
    }
    if (embedded && onOpenCodeFile) {
      onOpenCodeFile(relPath)
      return
    }
    const url = relPath
      ? `/srceditor/${encodeURIComponent(projectName)}?file=${encodeURIComponent(relPath)}&root=src`
      : `/srceditor/${encodeURIComponent(projectName)}?root=src`
    window.open(url, '_blank', 'noopener')
  }, [projectName, objectName, handleSave, embedded, onOpenCodeFile])

  // When embedded, report dirty state + register save so the workbench shell
  // can guard navigation away and offer "Save & Continue".
  useEffect(() => {
    if (!embedded || !onDirtyChange) return
    onDirtyChange(isDirty)
    return () => onDirtyChange(false)
  }, [embedded, isDirty, onDirtyChange])
  useEffect(() => {
    if (!embedded || !registerSave) return
    registerSave(handleSave)
  }, [embedded, registerSave, handleSave])

  const handleBack = useCallback(() => {
    const selectedNodeId = (location.state as { selectedNodeId?: string })?.selectedNodeId
    guard(() => navigate('/workbench', { state: { selectedNodeId } }))
  }, [guard, location.state, navigate])

  // Jump to the transaction designer for a transaction referenced by the
  // currently-selected embed block, and remember enough about where we are
  // so the user can return to this exact spot. We piggy-back the workbench
  // breadcrumb (selectedNodeId) so the Back button keeps walking the user
  // up the chain if they exit via the transaction designer.
  const handleOpenTransaction = useCallback((txnObjName: string) => {
    if (!projectName || !txnObjName) return
    const selectedNodeId = (location.state as { selectedNodeId?: string })?.selectedNodeId
    const smartPageScrollTop = canvasScrollRef.current?.scrollTop ?? 0
    guard(() => navigate(
      `/designers/transaction/${encodeURIComponent(projectName)}/${encodeURIComponent(txnObjName)}`,
      {
        state: {
          selectedNodeId,
          returnTo: 'smart-page' as const,
          returnProject: projectName,
          returnPage: objectName,
          smartPageSelection: selection,
          smartPageScrollTop,
        },
      },
    ))
  }, [projectName, objectName, selection, guard, location.state, navigate])

  const handleSaveAndContinue = useCallback(async () => {
    const ok = await handleSave()
    if (ok) confirmDiscard()
  }, [handleSave, confirmDiscard])

  // A link inside a SELECTED block was clicked (see BlockNode: the first
  // click on a link in an unselected block only selects it; a second click
  // — when already selected — activates the link). Following the link
  // leaves the designer, so confirm first: when there are unsaved changes
  // the discard modal both warns and offers Save & Continue; otherwise a
  // lightweight confirm. New-tab links keep the designer open, so they just
  // open without a prompt.
  const handleActivateLink = useCallback((href: string, newTab: boolean) => {
    if (!href) return
    if (newTab) {
      window.open(href, '_blank', 'noopener,noreferrer')
      return
    }
    const navigateAway = () => {
      allowNextUnload()              // skip the native beforeunload double-prompt
      window.location.assign(href)
    }
    if (isDirty) {
      guard(navigateAway)            // discard modal acts as the leave prompt
    } else if (window.confirm('Follow this link and leave the page designer?')) {
      navigateAway()
    }
  }, [isDirty, guard, allowNextUnload])

  // v2: advanced panel (sections, tokens, SEO, variants, navigation)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [previewViewport, setPreviewViewport] = useState<Viewport>('desktop')

  // Preview — auto-save the current page, then open the Angular runtime in an iframe modal
  const [previewOpen, setPreviewOpen] = useState(false)
  const handlePreview = useCallback(async () => {
    const ok = await handleSave()
    if (ok) setPreviewOpen(true)
  }, [handleSave])

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        handleSave()
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        handleUndo()
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault()
        handleRedo()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [handleSave, handleUndo, handleRedo])

  // ── Section helpers (canvas inline UX) ───────────────────────
  const addSectionAt = useCallback((index: number, tag: NonNullable<Section['tag']>) => {
    const cur = pageRef.current
    const sections = [...(cur.sections ?? [])]
    const headingId = nextBlockIdForType(cur, 'heading')
    const starter: Block | null =
      tag === 'header'
        ? ({ ...makeBlock('heading', headingId), level: 1, text: 'Your headline' } as Block)
        : tag === 'footer'
        ? ({ ...makeBlock('heading', headingId), level: 3, text: '© Your company' } as Block)
        : null
    const sec: Section = {
      id: newSectionId(),
      name: tag.charAt(0).toUpperCase() + tag.slice(1),
      tag,
      blocks: starter ? [starter] : [],
      // A pop-up section opens its blocks in a modal — seed the trigger config.
      ...(tag === 'popup'
        ? { popup: { trigger: { style: 'fab', label: 'Help', icon: '💬', variant: 'primary', position: 'bottom-right', offset: 24 }, modalSize: 'md' } }
        : {}),
    }
    sections.splice(Math.max(0, Math.min(index, sections.length)), 0, sec)
    const main = sections.find(s => (s.tag ?? 'main') === 'main')
    handlePageChange({ ...cur, sections, blocks: main?.blocks ?? cur.blocks })
    setSelection({ type: 'section', sectionId: sec.id })
  }, [handlePageChange])

  /**
   * Insert a pre-composed section from the template library.
   *
   * Ids are regenerated on the way in: a template is a VALUE that can be
   * inserted many times, and two sections (or two blocks) sharing an id
   * would break selection, per-block scoped CSS and anchor targets.
   */
  const addTemplateSectionAt = useCallback((index: number, template: Section) => {
    const cur = pageRef.current
    const sections = [...(cur.sections ?? [])]

    // Every anchor already on the page, so a template's anchors can't collide.
    // Collected with the canonical walker (it resolves child slots by block
    // TYPE) rather than reaching into `columns`/`items` generically — those
    // field names are overloaded across block types.
    const usedAnchors = new Set<string>()
    for (const s of sections) {
      if (s.anchorId) usedAnchors.add(s.anchorId)
      walkBlocks(s.blocks ?? [], (b) => { if (b.anchorId) usedAnchors.add(b.anchorId) })
    }

    const freshBlocks = (blocks: Block[] | undefined): Block[] =>
      (blocks || []).map((b) => {
        const next = { ...b, id: newId(b.type.slice(0, 3)) } as Block
        if (next.anchorId && usedAnchors.has(next.anchorId)) delete next.anchorId
        else if (next.anchorId) usedAnchors.add(next.anchorId)
        // `mapBlockSlots` recurses into whichever child slot THIS block type
        // owns (tab/accordion items, column blocks, or `children`).
        return mapBlockSlots(next, freshBlocks)
      })

    // An anchor id that already exists on the page is dropped rather than
    // duplicated — two elements with one id makes `scroll_to` ambiguous.
    let anchorId = template.anchorId
    if (anchorId && usedAnchors.has(anchorId)) anchorId = undefined

    const sec: Section = {
      ...template,
      id: newSectionId(),
      anchorId,
      blocks: freshBlocks(template.blocks),
    }

    // A template tagged header/footer can only be inserted once; fall back to
    // a plain section rather than producing a second one the renderers would
    // have to disambiguate.
    if ((sec.tag === 'header' && sections.some((s) => s.tag === 'header'))
      || (sec.tag === 'footer' && sections.some((s) => s.tag === 'footer'))) {
      sec.tag = 'custom'
    }

    sections.splice(Math.max(0, Math.min(index, sections.length)), 0, sec)
    const main = sections.find(s => (s.tag ?? 'main') === 'main')
    handlePageChange({ ...cur, sections, blocks: main?.blocks ?? cur.blocks })
    setSelection({ type: 'section', sectionId: sec.id })
  }, [handlePageChange])

  const removeSectionById = useCallback((sectionId: string) => {
    const cur = pageRef.current
    const sections = (cur.sections ?? []).filter(s => s.id !== sectionId)
    const main = sections.find(s => (s.tag ?? 'main') === 'main')
    handlePageChange({ ...cur, sections, blocks: main?.blocks ?? [] })
    setSelection({ type: 'page' })
  }, [handlePageChange])

  // ── Block helpers ──
  // These resolve a block's owning section (main / header / footer / custom)
  // before mutating, so slash menu, Enter-to-insert, and Backspace-to-merge
  // all work in any section the user types into.
  const updateBlock = useCallback((next: Block) => {
    const loc = findBlockLocation(pageRef.current, next.id)
    if (loc) {
      handlePageChange(updateBlockInSection(pageRef.current, loc.sectionId, next.id, next))
    } else {
      const blocks = pageRef.current.blocks.map((b) => (b.id === next.id ? next : b))
      handlePageChange({ ...pageRef.current, blocks })
    }
  }, [handlePageChange])

  const insertBlockAfter = useCallback((blockId: string, type: BlockType = 'paragraph') => {
    const loc = findBlockLocation(pageRef.current, blockId)
    if (loc) {
      const r = insertBlockInSectionAt(pageRef.current, loc.sectionId, loc.blockIndex + 1, type)
      handlePageChange(r.model)
      setSelection({ type: 'block', blockId: r.block.id, sectionId: loc.sectionId })
      if (['heading', 'paragraph', 'bullet_list', 'numbered_list', 'todo', 'quote', 'code'].includes(type)) {
        setTimeout(() => {
          const el = document.querySelector(`[data-block-id="${r.block.id}"] [contenteditable]`) as HTMLElement | null
          el?.focus()
        }, 0)
      }
      return
    }
    // Legacy v1 page (no sections): operate on the flat blocks array.
    const blocks = [...pageRef.current.blocks]
    const idx = blocks.findIndex((b) => b.id === blockId)
    const newBlock = makeBlock(type, nextBlockIdForType(pageRef.current, type), { pageName: pageRef.current.obj_name })
    blocks.splice(idx + 1, 0, newBlock)
    handlePageChange({ ...pageRef.current, blocks })
    setSelection({ type: 'block', blockId: newBlock.id })
    if (['heading', 'paragraph', 'bullet_list', 'numbered_list', 'todo', 'quote', 'code'].includes(type)) {
      setTimeout(() => {
        const el = document.querySelector(`[data-block-id="${newBlock.id}"] [contenteditable]`) as HTMLElement | null
        el?.focus()
      }, 0)
    }
  }, [handlePageChange])

  const insertBlockAtIndex = useCallback((idx: number, type: BlockType = 'paragraph') => {
    const mainId = findMainSectionId(pageRef.current)
    if (mainId) {
      const r = insertBlockInSectionAt(pageRef.current, mainId, idx, type)
      handlePageChange(r.model)
      setSelection({ type: 'block', blockId: r.block.id, sectionId: mainId })
      return
    }
    const blocks = [...pageRef.current.blocks]
    const newBlock = makeBlock(type, nextBlockIdForType(pageRef.current, type), { pageName: pageRef.current.obj_name })
    blocks.splice(idx, 0, newBlock)
    handlePageChange({ ...pageRef.current, blocks })
    setSelection({ type: 'block', blockId: newBlock.id })
  }, [handlePageChange])

  const deleteBlock = useCallback((blockId: string) => {
    const loc = findBlockLocation(pageRef.current, blockId)
    if (loc) {
      handlePageChange(deleteBlockInSection(pageRef.current, loc.sectionId, blockId))
      return
    }
    const blocks = pageRef.current.blocks.filter((b) => b.id !== blockId)
    if (blocks.length === 0) blocks.push(makeBlock('paragraph', nextBlockIdForType(pageRef.current, 'paragraph')))
    handlePageChange({ ...pageRef.current, blocks })
  }, [handlePageChange])

  // Slash command on text input
  const handleTextKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>, block: Block) => {
    const target = e.currentTarget as HTMLDivElement
    const text = target.innerText

    if (e.key === 'Enter' && !e.shiftKey && block.type !== 'code') {
      e.preventDefault()
      // List-like blocks: if empty line, convert this block to paragraph (Notion behavior)
      if ((block.type === 'bullet_list' || block.type === 'numbered_list' || block.type === 'todo') && text.trim() === '') {
        updateBlock(makeBlock('paragraph', block.id) as Block)
        return
      }
      const continueType: BlockType =
        block.type === 'bullet_list' || block.type === 'numbered_list' || block.type === 'todo'
          ? block.type
          : 'paragraph'
      insertBlockAfter(block.id, continueType)
      return
    }

    if (e.key === 'Backspace' && text === '' && block.type !== 'paragraph' && block.type !== 'embed' && block.type !== 'image' && block.type !== 'input_group' && block.type !== 'divider') {
      // Convert non-paragraph empty block to paragraph
      e.preventDefault()
      updateBlock(makeBlock('paragraph', block.id))
      return
    }

    if (e.key === 'Backspace' && text === '' && block.type === 'paragraph') {
      const loc = findBlockLocation(pageRef.current, block.id)
      const sectionBlocks = loc
        ? pageRef.current.sections?.find(s => s.id === loc.sectionId)?.blocks ?? pageRef.current.blocks
        : pageRef.current.blocks
      if (sectionBlocks.length > 1) {
        e.preventDefault()
        const idx = sectionBlocks.findIndex((b) => b.id === block.id)
        const prev = sectionBlocks[idx - 1]
        deleteBlock(block.id)
        if (prev) {
          setSelection({ type: 'block', blockId: prev.id, sectionId: loc?.sectionId })
          setTimeout(() => {
            const el = document.querySelector(`[data-block-id="${prev.id}"] [contenteditable]`) as HTMLElement | null
            if (el) {
              el.focus()
              const range = document.createRange()
              range.selectNodeContents(el)
              range.collapse(false)
              const sel = window.getSelection()
              sel?.removeAllRanges()
              sel?.addRange(range)
            }
          }, 0)
        }
        return
      }
    }

    if (e.key === '/' && (block.type === 'paragraph' || block.type === 'heading' || block.type === 'bullet_list' || block.type === 'numbered_list' || block.type === 'todo' || block.type === 'quote') && text === '') {
      // Open slash menu (inline mode reads query from contenteditable text)
      const rect = target.getBoundingClientRect()
      setSlashMenu({
        blockId: block.id,
        mode: 'inline',
        query: '',
        anchorLeft: rect.left,
        anchorTop: rect.top,
        anchorBottom: rect.bottom,
      })
      return
    }
  }, [insertBlockAfter, updateBlock, deleteBlock, handlePageChange])

  // Watch text changes to update slash query (only for inline-typed slash menu)
  useEffect(() => {
    if (!slashMenu || slashMenu.mode !== 'inline') return
    const handler = () => {
      const el = document.querySelector(`[data-block-id="${slashMenu.blockId}"] [contenteditable]`) as HTMLElement | null
      if (!el) return
      const text = el.innerText
      if (text.startsWith('/')) {
        setSlashMenu((prev) => prev ? { ...prev, query: text.slice(1) } : null)
      } else {
        setSlashMenu(null)
      }
    }
    document.addEventListener('input', handler)
    return () => document.removeEventListener('input', handler)
  }, [slashMenu])

  const handleSlashPick = useCallback((type: BlockType) => {
    if (!slashMenu) return
    const blockId = slashMenu.blockId
    setSlashMenu(null)
    // Replace the current block in-place, preserving id, in whichever
    // section it lives (main / header / footer / custom).
    const newBlock = makeBlock(type, blockId, { pageName: pageRef.current.obj_name }) as Block
    const loc = findBlockLocation(pageRef.current, blockId)
    if (loc) {
      handlePageChange(updateBlockInSection(pageRef.current, loc.sectionId, blockId, newBlock))
      setSelection({ type: 'block', blockId, sectionId: loc.sectionId })
    } else {
      const blocks = pageRef.current.blocks.map((b) => (b.id === blockId ? newBlock : b))
      handlePageChange({ ...pageRef.current, blocks })
      setSelection({ type: 'block', blockId })
    }
    setTimeout(() => {
      const el = document.querySelector(`[data-block-id="${blockId}"] [contenteditable]`) as HTMLElement | null
      if (el) {
        el.textContent = ''
        el.focus()
      }
    }, 0)
  }, [slashMenu, handlePageChange])

  const handleTransform = useCallback((blockId: string, anchor: { left: number; top: number; bottom: number }) => {
    setSelection({ type: 'block', blockId })
    setSlashMenu({
      blockId,
      mode: 'transform',
      query: '',
      anchorLeft: anchor.left,
      anchorTop: anchor.top,
      anchorBottom: anchor.bottom,
    })
  }, [])

  // Drag/drop
  const handleDragStart = useCallback((e: React.DragEvent, blockId: string) => {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', blockId)
    dragResolvedRef.current = false

    // Custom drag image: a clear, accent-coloured pill with the block label so
    // the user always sees what's being dragged (the browser's default ghost
    // is sometimes invisible/transparent depending on the source element).
    const block = pageRef.current.blocks.find(b => b.id === blockId)
    const label = block ? humanBlockLabel(block.type) : 'Block'
    const ghost = document.createElement('div')
    ghost.textContent = `⇅  Moving ${label}`
    ghost.style.cssText = [
      'position:absolute', 'top:-1000px', 'left:-1000px',
      'padding:8px 14px', 'border-radius:999px',
      'background:var(--pg-accent, #4c8dff)', 'color:#fff',
      'font-size:14px', 'font-weight:600',
      'box-shadow:0 6px 18px rgba(0,0,0,0.25)',
      'pointer-events:none', 'white-space:nowrap',
    ].join(';')
    document.body.appendChild(ghost)
    e.dataTransfer.setDragImage(ghost, 16, 16)
    // Keep the node in the DOM long enough for the browser to snapshot it.
    setTimeout(() => ghost.remove(), 0)

    setDraggedBlockId(blockId)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent, blockId: string) => {
    if (!draggedBlockId) return
    // Allow hovering over the source block (so the cursor shows "drop"
    // even though we don't draw an indicator there).
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (draggedBlockId === blockId) {
      setDropTarget(null)
      return
    }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const pos: 'before' | 'after' = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after'
    setDropTarget({ id: blockId, pos })
  }, [draggedBlockId])

  const handleDrop = useCallback((e: React.DragEvent, targetId: string) => {
    e.preventDefault()
    if (!draggedBlockId) {
      dragResolvedRef.current = true
      return
    }
    if (draggedBlockId === targetId) {
      toast.error("Can't drop a block on itself — drop above or below another block.")
      dragResolvedRef.current = true
      setDraggedBlockId(null)
      setDropTarget(null)
      return
    }
    const fromLoc = findBlockLocation(pageRef.current, draggedBlockId)
    const toLoc = findBlockLocation(pageRef.current, targetId)
    if (!fromLoc) {
      toast.error('Could not move block: source block not found.')
      dragResolvedRef.current = true
      setDraggedBlockId(null)
      setDropTarget(null)
      return
    }
    if (!toLoc) {
      toast.error('Could not move block: drop target not found.')
      dragResolvedRef.current = true
      setDraggedBlockId(null)
      setDropTarget(null)
      return
    }
    const insertIdx = dropTarget?.pos === 'after' ? toLoc.blockIndex + 1 : toLoc.blockIndex
    const next = moveBlockToSectionAt(pageRef.current, draggedBlockId, toLoc.sectionId, insertIdx)
    handlePageChange(next)
    // Keep selection on the moved block in its new section.
    setSelection({ type: 'block', blockId: draggedBlockId, sectionId: toLoc.sectionId })
    dragResolvedRef.current = true
    setDraggedBlockId(null)
    setDropTarget(null)
  }, [draggedBlockId, dropTarget, handlePageChange])

  const handleDragEnd = useCallback(() => {
    // dragend always fires after a drag, regardless of whether drop landed on
    // a valid target. If the drop never resolved (no drop event fired or it
    // bailed out before reaching the toast), tell the user why.
    if (!dragResolvedRef.current && draggedBlockId) {
      toast.error('Drop cancelled — release the block over another block to reorder.')
    }
    dragResolvedRef.current = true
    setDraggedBlockId(null)
    setDropTarget(null)
  }, [draggedBlockId])

  // Section-level fallback so drops landing in dead space inside the main
  // section (gap above first / below last block, section padding, the
  // "Add block" pill) still commit using the last hovered indicator. The
  // per-block handlers above remain authoritative when the cursor is over
  // a block; this only fires when the drop bubbles up to the section.
  const handleSectionDragOver = useCallback((e: React.DragEvent) => {
    if (!draggedBlockId) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [draggedBlockId])

  const handleSectionDrop = useCallback((e: React.DragEvent) => {
    if (!draggedBlockId) return
    e.preventDefault()
    if (dragResolvedRef.current) return
    const target = dropTargetRef.current
    if (!target) {
      // No block was hovered — nothing to anchor against. Let dragend toast.
      return
    }
    handleDrop(e, target.id)
  }, [draggedBlockId, handleDrop])

  // Resizers
  const handleLeftResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.pageX
    const startWidth = leftWidth
    const onMove = (ev: MouseEvent) => {
      const delta = ev.pageX - startX
      setLeftWidth(Math.max(180, Math.min(420, startWidth + delta)))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [leftWidth])

  const handleRightResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.pageX
    const startWidth = rightWidth
    const onMove = (ev: MouseEvent) => {
      const delta = startX - ev.pageX
      setRightWidth(Math.max(220, Math.min(520, startWidth + delta)))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [rightWidth])

  const showLeft = layout === 'all' || layout === 'left-center'
  const showRight = layout === 'all' || layout === 'center-right'

  const inputValuesMemo = useMemo(() => inputValues, [inputValues])

  // Context that nested-block UIs (ChildBlockList, used inside Tabs /
  // Accordion / Columns / Container / Card canvases) consume to render
  // children with the same conventions used at the section level.
  const nestedCtxValue = useMemo(() => ({
    page,
    selection,
    onSelectBlock: (blockId: string) => setSelection({ type: 'block', blockId }),
    inputs: page.inputs,
    inputValues: inputValuesMemo,
    project: projectName,
    onInputValueChange: (name: string, value: string) =>
      setInputValues(prev => ({ ...prev, [name]: value })),
  }), [page, selection, inputValuesMemo, projectName])

  // Resolved theme — sparse PageTheme overrides merged with the active
  // preset. Drives canvas wrapper styles + CSS variables for blocks.
  const resolvedTheme = useMemo(() => resolveTheme(page.theme), [page.theme])

  // ─── Action runtime state ────────────────────────────
  // Per-action {loading, error, response, status}. Updated by the runner;
  // surfaced as {{actionName.loading|error|status|<json.path>}} via varsResolver.
  const [actionState, setActionState] = useState<Record<string, ActionState>>({})
  const actionStateRef = useRef(actionState)
  actionStateRef.current = actionState

  // Field errors (e.g. set_field_error step). Cleared on input edit + at action start.
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  // Live registry of input DOM nodes by input.name (populated by InputRenderer).
  const fieldRefsRef = useRef<Map<string, HTMLElement>>(new Map())

  // Refs that always point to the latest values — actions are async.
  const inputValuesRef = useRef(inputValues)
  inputValuesRef.current = inputValues

  // AbortController per running action; cancelled on unmount.
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map())

  // ─── Runtime callbacks ──────────────────────────────
  const setActionStateOne = useCallback((name: string, st: ActionState) => {
    setActionState((prev) => ({ ...prev, [name]: st }))
  }, [])
  const setInputCb = useCallback((name: string, value: string) => {
    setInputValues((prev) => ({ ...prev, [name]: value }))
  }, [])
  const setVarRawCb = useCallback((name: string, value: unknown) => {
    setInputValues((prev) => ({ ...prev, [name]: value }))
  }, [])
  const clearInputsCb = useCallback((names?: string[]) => {
    setInputValues((prev) => {
      if (!names || names.length === 0) {
        const next: Record<string, string> = {}
        for (const inp of pageRef.current.inputs) next[inp.name] = ''
        return next
      }
      const next = { ...prev }
      for (const n of names) next[n] = ''
      return next
    })
  }, [])
  const setFieldErrorCb = useCallback((name: string, message: string) => {
    setFieldErrors((prev) => ({ ...prev, [name]: message }))
  }, [])
  const clearFieldErrorCb = useCallback((name: string) => {
    setFieldErrors((prev) => {
      if (!(name in prev)) return prev
      const next = { ...prev }
      delete next[name]
      return next
    })
  }, [])
  const clearAllFieldErrorsCb = useCallback(() => setFieldErrors({}), [])
  const focusInputCb = useCallback((name: string) => {
    const el = fieldRefsRef.current.get(name)
    if (!el) {
      if (import.meta.env.DEV) console.warn(`[page] focus_input: '${name}' is not mounted`)
      return false
    }
    el.focus?.()
    el.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    return true
  }, [])
  const showMessageCb = useCallback((level: 'success' | 'error' | 'info', text: string) => {
    if (level === 'success') toast.success(text)
    else if (level === 'error') toast.error(text)
    else toast(text)
  }, [])
  const navigateToCb = useCallback((objType: string, objName: string) => {
    if (!projectName || !objName) return
    // Open the destination designer in a new browser tab so any unsaved
    // edits in this page (theme, blocks, inputs, actions) are preserved.
    // The previous behaviour used react-router's navigate(), which unmounted
    // the page designer and discarded in-memory state.
    const proj = encodeURIComponent(projectName)
    const obj = encodeURIComponent(objName)
    let url: string | null = null
    switch (objType) {
      case 'T': url = `/designers/transaction/${proj}/${obj}`; break
      case 'C': url = `/designers/page/${proj}/${obj}`; break
      case 'W': url = `/designers/workflow/${proj}/${obj}`; break
      case 'D': url = `/designers/dashboard/${proj}/${obj}`; break
      case 'V': url = `/designers/visual/${proj}/${obj}`; break
      default:  url = '/workbench'; break
    }
    window.open(url, '_blank', 'noopener')
  }, [projectName])

  const runActionByNameCb = useCallback(async (name: string) => {
    const action = (pageRef.current.actions || []).find((a) => a.name === name)
    if (!action) {
      toast.error(`Action "${name}" not found`)
      return
    }
    if (actionStateRef.current[name]?.loading) return  // already running
    clearAllFieldErrorsCb()

    const ac = new AbortController()
    abortControllersRef.current.set(name, ac)

    const resolve = buildResolver({
      values: inputValuesRef.current,
      pageMeta: {
        title: pageRef.current.title || '',
        description: pageRef.current.description || '',
        'page.title': pageRef.current.title || '',
        'page.description': pageRef.current.description || '',
      },
      actionState: actionStateRef.current,
    })

    const ctx: ActionCtx = {
      inputs: pageRef.current.inputs,
      values: inputValuesRef.current,
      setInput: setInputCb,
      setVarRaw: setVarRawCb,
      clearInputs: clearInputsCb,
      setFieldError: setFieldErrorCb,
      clearFieldError: clearFieldErrorCb,
      focusInput: focusInputCb,
      setActionState: setActionStateOne,
      getActionState: (k) => actionStateRef.current[k],
      resolve,
      showMessage: showMessageCb,
      navigate: navigateToCb,
      signal: ac.signal,
      // IOFlow-routed call_api steps go through the platform invoke proxy.
      integrationInvokeUrl: (apiName) =>
        `${APP_BASE}/api/integration/invoke/${encodeURIComponent(projectName)}/${encodeURIComponent(apiName)}`,
    }
    try {
      await runAction(action, ctx)
    } finally {
      abortControllersRef.current.delete(name)
    }
  }, [
    setInputCb, setVarRawCb, clearInputsCb, setFieldErrorCb, clearFieldErrorCb, focusInputCb,
    setActionStateOne, showMessageCb, navigateToCb, clearAllFieldErrorsCb,
  ])

  // Abort all in-flight actions on unmount.
  useEffect(() => {
    return () => {
      for (const ac of abortControllersRef.current.values()) ac.abort()
      abortControllersRef.current.clear()
    }
  }, [])

  // ─── Context values exposed to the canvas ────────────

  const runtime: PageRuntime = useMemo(() => ({
    runActionByName: runActionByNameCb,
    getActionState: (k) => actionStateRef.current[k],
    setInput: setInputCb,
    clearInputs: clearInputsCb,
    navigateTo: navigateToCb,
    showMessage: showMessageCb,
    getInputs: () => pageRef.current.inputs,
    getInputValues: () => inputValuesRef.current,
    getActions: () => pageRef.current.actions || [],
    getProject: () => projectName,
  }), [runActionByNameCb, setInputCb, clearInputsCb, navigateToCb, showMessageCb, projectName])

  // Variable resolver: covers inputs, page meta, and action-state lookups
  // (including {{actionName.loading|error|status}} and {{key.dotted.path}}).
  const varsResolver = useCallback((key: string) => {
    if (Object.prototype.hasOwnProperty.call(inputValues, key)) return inputValues[key]
    if (key === 'title' || key === 'page.title') return page.title || ''
    if (key === 'description' || key === 'page.description') return page.description || ''
    const dot = key.indexOf('.')
    if (dot > 0) {
      const head = key.slice(0, dot)
      const tail = key.slice(dot + 1)
      const st = actionState[head]
      if (st) {
        if (tail === 'loading') return st.loading ? 'true' : 'false'
        if (tail === 'error')   return st.error || ''
        if (tail === 'status')  return st.status === undefined ? '' : String(st.status)
        const v = getPath(st.response, tail)
        return v === undefined ? undefined : toDisplayString(v)
      }
    }
    const st = actionState[key]
    if (st) return toDisplayString(st.response)
    return undefined
  }, [inputValues, page.title, page.description, actionState])

  const fieldErrorsApi = useMemo(() => ({
    errors: fieldErrors,
    setError: setFieldErrorCb,
    clearError: clearFieldErrorCb,
    clearAll: clearAllFieldErrorsCb,
  }), [fieldErrors, setFieldErrorCb, clearFieldErrorCb, clearAllFieldErrorsCb])

  const fieldRefsApi = useMemo(() => ({
    register: (name: string, el: HTMLElement | null) => {
      if (el) fieldRefsRef.current.set(name, el)
      else fieldRefsRef.current.delete(name)
    },
    focus: focusInputCb,
  }), [focusInputCb])

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100%' }}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--pg-accent)' }} />
          <span style={{ color: 'var(--pg-text-secondary)' }}>Loading page…</span>
        </div>
      </div>
    )
  }

  return (
    <PageRuntimeContext.Provider value={runtime}>
    <PageFieldErrorsContext.Provider value={fieldErrorsApi}>
    <PageFieldRefsContext.Provider value={fieldRefsApi}>
    <PageVarsContext.Provider value={varsResolver}>
    <NestedBlockContext.Provider value={nestedCtxValue}>
    <div style={embedded && embedFullscreen ? {
      position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--pg-bg, var(--color-bg))',
      display: 'flex', flexDirection: 'column', padding: '0 12px',
    } : {
      display: 'flex', flexDirection: 'column', height: '100%',
    }}>
      {/* Toolbar (project + title + actions) */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '4px 0', flexShrink: 0,
      }}>
        <div
          className="flex items-center gap-2"
          onClick={() => setSelection({ type: 'page' })}
          style={{ cursor: 'pointer', minWidth: 0 }}
          title="Edit page properties in the right panel"
        >
          {!embedded && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={(e) => { e.stopPropagation(); handleBack() }}
            >
              <ArrowLeft size={16} />
            </button>
          )}
          <StickyNote size={18} style={{ color: 'var(--pg-accent)', flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <h2 style={{
              fontSize: '1.0625rem', fontWeight: 600, margin: 0,
              color: 'var(--pg-text)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {objectName || 'Page Designer'}
            </h2>
            <p style={{
              color: 'var(--pg-text-secondary)', fontSize: '0.8125rem', margin: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {projectName} · smart page
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button className="btn btn-ghost btn-sm" onClick={handleUndo} disabled={undoCount === 0} title="Undo">
            <Undo2 size={15} />
          </button>
          <button className="btn btn-ghost btn-sm" onClick={handleRedo} disabled={redoCount === 0} title="Redo">
            <Redo2 size={15} />
          </button>
          <div style={{ width: 1, height: 20, background: 'var(--pg-border)' }} />
          <DeviceToggle value={previewViewport} onChange={setPreviewViewport} />
          <div style={{ width: 1, height: 20, background: 'var(--pg-border)' }} />
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setAdvancedOpen(true)}
            title="Sections, tokens, SEO, A/B variants, navigation"
          >
            <Settings size={14} />
            <span style={{ fontSize: '0.8625rem' }}>Advanced</span>
          </button>
          <div style={{ width: 1, height: 20, background: 'var(--pg-border)' }} />
          <button
            className="btn btn-ghost btn-sm"
            onClick={handlePreview}
            disabled={saving}
            title="Save and preview in runtime"
          >
            <Eye size={14} />
            <span style={{ fontSize: '0.8625rem' }}>Preview</span>
          </button>
          <div style={{ width: 1, height: 20, background: 'var(--pg-border)' }} />
          {canEdit ? (
            <button
              className={`btn btn-sm ${isDirty ? 'btn-primary' : 'btn-secondary'}`}
              onClick={handleSave}
              disabled={saving}
              title={isDirty ? 'You have unsaved changes' : 'No changes to save'}
              style={isDirty ? { boxShadow: '0 0 0 3px rgba(76,141,255,0.35)' } : undefined}
            >
              {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
              {saving ? 'Saving…' : isDirty ? 'Save *' : 'Save'}
            </button>
          ) : (
            <ReadOnlyBadge />
          )}
          {embedded && !hostFullscreen && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setEmbedFullscreen((f) => !f)}
              title={embedFullscreen ? 'Restore' : 'Full screen'}
            >
              {embedFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
          )}
          <LayoutKebabMenu value={layout} onChange={setLayout} />
        </div>
      </div>

      <AdvancedPanel
        open={advancedOpen}
        onClose={() => setAdvancedOpen(false)}
        project={projectName}
        page={page}
        onChange={handlePageChange}
      />

      {/* Body */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', borderTop: '1px solid var(--pg-border)' }}>
        {/* Left: outline */}
        {showLeft && (
          <>
            <div style={{ width: leftWidth, minWidth: 180, flexShrink: 0, overflowY: 'auto', background: 'var(--pg-bg)' }}>
              <Outline
                page={page}
                selection={selection}
                onSelect={setSelection}
                onAddBlock={() => insertBlockAtIndex(page.blocks.length, 'paragraph')}
              />
            </div>
            <div onMouseDown={handleLeftResize} style={resizerStyle}>
              <div style={resizerLine} />
            </div>
          </>
        )}

        {/* Center: canvas — theme-aware wrapper exposes palette/typography
            as CSS variables; all blocks inside inherit them. */}
        <div
          ref={canvasScrollRef}
          style={{
            flex: 1, overflow: 'auto',
            // Background: solid palette color when layout.background.kind
            // is 'none', otherwise the gradient/image returned by
            // backgroundCss(). Spread `backgroundCss` LAST so any
            // longhand (backgroundImage, backgroundSize) it emits isn't
            // clobbered by the `background` shorthand below.
            //
            // Chrome (header/footer) pages instead use chromeBackgroundCss:
            // their intended color is `palette.background`, and the preset's
            // default solid light `layout.background` would otherwise clobber
            // a dark bar white (leaving dark-theme text invisible). This is
            // the same rule the SSR ChromePage + in-app renderChrome apply,
            // so the canvas matches the deployed output.
            ...themeCssVars(resolvedTheme),
            ...(isChromePage
              ? chromeBackgroundCss(resolvedTheme)
              : {
                  background: resolvedTheme.palette.background,
                  ...backgroundCss(resolvedTheme.layout.background),
                  ...pageParallaxStyle(resolvedTheme.layout.background),
                }),
          }}
        >
          {/* Parallax keyframes/class for scroll-driven background panning.
              This scroll container is the `view()` timeline scrollport, so
              parallax animates live as the canvas scrolls. Injected once here
              since the canvas doesn't go through ThemedPageShell. */}
          <style dangerouslySetInnerHTML={{ __html: PARALLAX_CSS }} />
          {/* The shared page-runtime stylesheet ThemedPageShell would have
              injected: nav drawer, widgets, chrome. Without it the canvas
              drew the nav_menu's hidden drawer checkbox and an unstyled
              burger. */}
          <style dangerouslySetInnerHTML={{ __html: pageRuntimeCss() }} />
          {/* Inherited site header (external pages): a read-only preview of
              the site-wide header Smart Page so the canvas matches what
              ships. Honors the per-page navigation override. */}
          {page.kind === 'external' && (
            <InheritedChromeBand projectName={projectName} page={page} slot="header" />
          )}
          <SiteMenuContext.Provider value={siteMenu}>
          <SiteMenusContext.Provider value={siteMenus}>
          <ViewportContext.Provider value={previewViewport}>
          <div className="pg-canvas" style={{
            // Width strategy: cap at the theme's content-width on
            // desktop / fixed device width on tablet+mobile. On
            // desktop, only carve out the *minimum* extra centering
            // room needed for the block hover handles (drag, +,
            // transform — ≈80px wide, sitting at `right: calc(100% +
            // 6px)` of each block). The handles already get most of
            // the room they need from the theme's `paddingX` — they
            // only overflow pg-canvas leftward when paddingX < 80.
            // So the gutter we reserve here is `max(0, 80 - paddingX)`
            // per side, doubled for total. With the default
            // paddingX=60, that's 40px total — barely noticeable —
            // instead of the previous 160px which was making desktop
            // look narrower than tablet on small editor panels.
            width: previewViewport === 'desktop'
              ? `calc(100% - ${2 * Math.max(0, 80 - resolvedTheme.layout.paddingX)}px)`
              : '100%',
            maxWidth: previewViewport === 'desktop'
              ? (resolvedTheme.layout.maxWidth ?? CONTENT_WIDTH_PX[resolvedTheme.layout.contentWidth])
              : viewportWidth(previewViewport),
            margin: '0 auto',
            // Honor the theme's `paddingX` symmetrically on both sides —
            // that's the page's actual margin and is what the published
            // page will look like.
            padding: `${resolvedTheme.layout.paddingY}px ${resolvedTheme.layout.paddingX}px ${resolvedTheme.layout.paddingY + 20}px ${resolvedTheme.layout.paddingX}px`,
            color: resolvedTheme.palette.text,
            fontFamily: FONT_STACKS[resolvedTheme.typography.fontFamily],
            fontSize: `${resolvedTheme.typography.baseSize}px`,
            lineHeight: resolvedTheme.typography.lineHeight,
            fontWeight: resolvedTheme.typography.fontWeight,
          }}>
            {/* Sections are first-class. Click any section's tag chip to
                edit its settings on the right. Click any block to edit it.
                Use the dashed +Add a section between sections to insert a
                Header/Footer/Aside. Click "+Add block" inside a section to
                add content. */}
            {(() => {
              const all = page.sections ?? []
              const hasHeader = all.some(s => s.tag === 'header')
              const hasFooter = all.some(s => s.tag === 'footer')
              const hasPopup = all.some(s => s.tag === 'popup')
              const out: React.ReactNode[] = []
              for (let i = 0; i <= all.length; i++) {
                const position: 'top' | 'middle' | 'bottom' =
                  i === 0 ? 'top' : i === all.length ? 'bottom' : 'middle'
                out.push(
                  <AddSectionButton
                    key={`add-${i}`}
                    position={position}
                    hasHeader={hasHeader}
                    hasFooter={hasFooter}
                    hasPopup={hasPopup}
                    onAdd={(tag) => addSectionAt(i, tag)}
                    onAddTemplate={(tpl) => addTemplateSectionAt(i, tpl)}
                  />
                )
                if (i < all.length) {
                  const sec = all[i]
                  const isMain = (sec.tag ?? 'main') === 'main'
                  out.push(
                    <SectionStrip
                      key={sec.id}
                      section={sec}
                      selected={selection.type === 'section' && selection.sectionId === sec.id}
                      selection={selection}
                      inputs={page.inputs}
                      inputValues={inputValuesMemo}
                      project={projectName}
                      designTokens={page.designTokens}
                      onSelectSection={() => setSelection({ type: 'section', sectionId: sec.id })}
                      onSelectBlock={(blockId) => setSelection({ type: 'block', blockId, sectionId: sec.id })}
                      onActivateLink={handleActivateLink}
                      onUpdateBlock={(next) => handlePageChange(updateBlockInSection(pageRef.current, sec.id, next.id, next))}
                      onInsertBlockAt={(idx, type) => {
                        const r = insertBlockInSectionAt(pageRef.current, sec.id, idx, type)
                        handlePageChange(r.model)
                        setSelection({ type: 'block', blockId: r.block.id, sectionId: sec.id })
                      }}
                      onDeleteBlock={(blockId) => handlePageChange(deleteBlockInSection(pageRef.current, sec.id, blockId))}
                      onRemoveSection={isMain ? undefined : () => removeSectionById(sec.id)}
                      // Slash menu, Enter-to-insert, and Backspace work in
                      // every section — the helpers resolve the owning
                      // section internally via findBlockLocation.
                      onTextKeyDown={handleTextKeyDown}
                      onTransform={(block, anchor) => handleTransform(block.id, anchor)}
                      // Drag-to-reorder works in every section, and blocks
                      // can move across sections (e.g. main → footer) too.
                      onDragStart={(e, blockId) => handleDragStart(e, blockId)}
                      onDragOver={(e, blockId) => handleDragOver(e, blockId)}
                      onDrop={(e, blockId) => handleDrop(e, blockId)}
                      onDragEnd={handleDragEnd}
                      onSectionDragOver={handleSectionDragOver}
                      onSectionDrop={handleSectionDrop}
                      dropTarget={dropTarget}
                      // Embed live-preview row selection writes
                      // selectedIdVar / selectedDataVar straight into
                      // the page-level input map; downstream embed
                      // filters then re-resolve on the next render.
                      onSetInputValue={(name, value) =>
                        setInputValues(prev => ({ ...prev, [name]: value }))
                      }
                    />
                  )
                }
              }
              return out
            })()}
          </div>
          </ViewportContext.Provider>
          </SiteMenusContext.Provider>
          </SiteMenuContext.Provider>
          {/* Inherited site footer (external pages) — read-only preview. */}
          {page.kind === 'external' && (
            <InheritedChromeBand projectName={projectName} page={page} slot="footer" />
          )}
        </div>

        {/* Right: properties */}
        {showRight && (
          <>
            <div onMouseDown={handleRightResize} style={resizerStyle}>
              <div style={resizerLine} />
            </div>
            <div style={{ width: rightWidth, minWidth: 220, flexShrink: 0, overflowY: 'auto', background: 'var(--pg-bg)', borderLeft: '1px solid var(--pg-border)' }}>
              <PropertyPanel
                project={projectName}
                page={page}
                selection={selection}
                transactions={transactions}
                siteMenu={siteMenu}
                siteMenus={siteMenus}
                siteEditUrl={siteNodeId
                  ? `/workbench/${encodeURIComponent(projectName)}/screens?obj=${encodeURIComponent(siteNodeId)}`
                  : undefined}
                onSiteMenusChanged={() => setSiteMenusEpoch((e) => e + 1)}
                onPageChange={handlePageChange}
                onSelectInput={(idx) => setSelection({ type: 'input', inputIndex: idx })}
                onSelectionChange={setSelection}
                onOpenTransaction={handleOpenTransaction}
                onOpenCode={handleOpenCode}
              />
            </div>
          </>
        )}
      </div>

      {slashMenu && (
        <SlashMenu
          query={slashMenu.query}
          anchor={{ left: slashMenu.anchorLeft, top: slashMenu.anchorTop, bottom: slashMenu.anchorBottom }}
          showSearch={slashMenu.mode === 'transform'}
          onPick={handleSlashPick}
          onClose={() => setSlashMenu(null)}
        />
      )}


      {/* placeholder rendering for contentEditable */}
      <style>{`
        [contenteditable][data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: var(--pg-text-secondary);
          opacity: 0.5;
          pointer-events: none;
        }
      `}</style>

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        projectName={projectName}
        objectName={objectName}
        viewport={previewViewport}
      />

      <DiscardChangesModal
        open={showDiscardModal}
        onCancel={cancelDiscard}
        onDiscard={confirmDiscard}
        onSave={handleSaveAndContinue}
        saving={saving}
      />
    </div>
    </NestedBlockContext.Provider>
    </PageVarsContext.Provider>
    </PageFieldRefsContext.Provider>
    </PageFieldErrorsContext.Provider>
    </PageRuntimeContext.Provider>
  )
}

// ─── Outline (left panel) ───────────────────────────────

function Outline({
  page,
  selection,
  onSelect,
  onAddBlock,
}: {
  page: PageModel
  selection: Selection
  onSelect: (s: Selection) => void
  onAddBlock: () => void
}) {
  return (
    <div style={{ padding: '12px 8px' }}>
      <button
        onClick={() => onSelect({ type: 'page' })}
        style={outlineButtonStyle(selection.type === 'page')}
      >
        <Settings size={13} /> Page
      </button>
      <button
        onClick={() => onSelect({ type: 'inputs' })}
        style={outlineButtonStyle(selection.type === 'inputs' || selection.type === 'input')}
      >
        <Sliders size={13} /> Variables
        <span style={{ marginLeft: 'auto', fontSize: '0.8125rem', color: 'var(--pg-text-secondary)' }}>
          {page.inputs.length}
        </span>
      </button>

      <div style={{
        marginTop: 14, marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 6px', fontSize: '0.8125rem', textTransform: 'uppercase',
        color: 'var(--pg-text-secondary)', fontWeight: 600,
      }}>
        <span>Sections ({(page.sections ?? []).length})</span>
        <button onClick={onAddBlock} title="Add block to main" className="btn btn-ghost btn-sm" style={{ padding: 2 }}>
          <Plus size={12} />
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {(page.sections ?? []).map((sec) => {
          const tag = (sec.tag ?? 'main') as 'header' | 'main' | 'footer' | 'aside' | 'popup' | 'custom'
          const isMain = tag === 'main'
          const sectionSelected = selection.type === 'section' && selection.sectionId === sec.id
          const tagColor =
            tag === 'header' ? '#3b82f6' :
            tag === 'footer' ? '#6366f1' :
            tag === 'aside'  ? '#a855f7' :
            tag === 'popup'  ? '#14b8a6' :
            isMain ? '#10b981' : '#8b5cf6'
          const tagLabel =
            tag === 'header' ? 'Header' :
            tag === 'footer' ? 'Footer' :
            tag === 'aside'  ? 'Aside' :
            tag === 'popup'  ? 'Pop-up' :
            isMain ? 'Main' : (sec.name || 'Section')
          return (
            <div key={sec.id}>
              <button
                onClick={() => onSelect({ type: 'section', sectionId: sec.id })}
                style={{
                  ...outlineButtonStyle(sectionSelected),
                  fontWeight: 600, fontSize: '0.8425rem',
                  borderLeft: `3px solid ${tagColor}`,
                  paddingLeft: 6,
                }}
                title={`Click to select ${tagLabel}`}
              >
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {tagLabel}
                </span>
                <span style={{ fontSize: '0.8125rem', fontWeight: 400, color: 'var(--pg-text-secondary)' }}>
                  {sec.blocks.length}
                </span>
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingLeft: 14 }}>
                {sec.blocks.map((b, i) => (
                  <button
                    key={b.id}
                    onClick={() => onSelect({ type: 'block', blockId: b.id, sectionId: sec.id })}
                    style={{
                      ...outlineButtonStyle(selection.type === 'block' && selection.blockId === b.id),
                      paddingLeft: 6,
                    }}
                  >
                    <FileText size={12} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8425rem' }}>
                      {summarizeBlock(b, i)}
                    </span>
                  </button>
                ))}
                {sec.blocks.length === 0 && (
                  <span style={{
                    fontSize: '0.8125rem', color: 'var(--pg-text-secondary)',
                    padding: '4px 8px', fontStyle: 'italic',
                  }}>
                    Empty
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function outlineButtonStyle(active: boolean): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: 8,
    width: '100%', padding: '6px 8px', fontSize: '0.8625rem',
    background: active ? 'var(--color-accent-weak)' : 'transparent',
    color: active ? 'var(--pg-accent)' : 'var(--pg-text)',
    border: 'none', borderRadius: 4, cursor: 'pointer',
    textAlign: 'left',
  }
}

function summarizeBlock(b: Block, i: number): string {
  const trim = (s: string, n = 30) => (s.length > n ? s.slice(0, n) + '…' : s)
  switch (b.type) {
    case 'heading': return trim(b.text || `Heading ${i + 1}`)
    case 'paragraph': return trim(b.text || `Paragraph ${i + 1}`)
    case 'bullet_list': {
      const first = b.items?.[0]
      const label = first?.text || `Bulleted list (${b.items?.length ?? 0} item${(b.items?.length ?? 0) === 1 ? '' : 's'})`
      return '• ' + trim(label)
    }
    case 'numbered_list': {
      const first = b.items?.[0]
      const label = first?.text || `Numbered list (${b.items?.length ?? 0} item${(b.items?.length ?? 0) === 1 ? '' : 's'})`
      return '1. ' + trim(label)
    }
    case 'todo': {
      const first = b.items[0]
      const checked = first?.checked ? '☑ ' : '☐ '
      const label = first?.text || `To-do list (${b.items?.length ?? 0} item${b.items.length === 1 ? '' : 's'})`
      return checked + trim(label)
    }
    case 'quote': return '❝ ' + trim(b.text || 'Quote')
    case 'code': return '</> ' + trim(b.text || 'Code')
    case 'divider': return '— Divider'
    case 'image': return '🖼 ' + (b.caption || b.alt || b.url || 'Image')
    case 'video': return '▶ ' + (b.caption || b.url || 'Video')
    case 'gallery': return `🗂 Gallery (${b.items?.length ?? 0})`
    case 'logo_cloud': return `⬢ Logos (${b.items?.length ?? 0})`
    case 'hero': return '🎯 ' + trim(b.title || 'Hero')
    case 'cta_banner': return '📣 ' + trim(b.title || 'CTA banner')
    case 'spacer': return `↕ Spacer (${b.height}px)`
    case 'columns': return `▥ Columns (${b.count})`
    case 'container': return '▢ Container'
    case 'card': return '🃏 ' + trim(b.card?.title || 'Card')
    case 'card_grid': return `▦ Card grid (${b.cards?.length ?? 0})`
    case 'feature_grid': return `✨ Features (${b.items?.length ?? 0})`
    case 'stats': return `# Stats (${b.items?.length ?? 0})`
    case 'testimonial': return '❝ ' + trim(b.authorName || b.quote || 'Testimonial')
    case 'pricing': return `$ Pricing (${b.tiers?.length ?? 0})`
    case 'faq': return `❓ FAQ (${b.items?.length ?? 0})`
    case 'tabs': return `⊟ Tabs (${b.items?.length ?? 0})`
    case 'accordion': return `☰ Accordion (${b.items?.length ?? 0})`
    case 'callout': return `💡 ${trim(b.title || b.body || 'Callout')}`
    case 'button': return '▣ ' + trim(b.button?.label || 'Button')
    case 'button_group': return `▥ Buttons (${b.buttons?.length ?? 0})`
    case 'newsletter': return '✉ ' + trim(b.heading || 'Newsletter')
    case 'contact_form': return '📝 ' + trim(b.heading || 'Contact form')
    case 'input_group': return `⌨ Inputs (${b.inputs?.length ?? 0})`
    case 'embed': return `⊞ Embed: ${b.embed?.obj_name || '(unset)'} · ${b.embed?.mode ?? '?'}`
    default: return `Block ${i + 1}`
  }
}

// Workspace layout picker as a 3-dot dropdown anchored to the top toolbar.
// Replaces the old fixed bottom-right pill that was overlapping with the
// property panel checkboxes when the right panel grew tall.
function LayoutKebabMenu({
  value,
  onChange,
}: {
  value: LayoutMode
  onChange: (m: LayoutMode) => void
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  // Click-outside / Escape to close, so the menu doesn't linger when the
  // user moves on to another part of the toolbar.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const items: Array<{ mode: LayoutMode; label: string; icon: React.ReactNode }> = [
    { mode: 'all',          label: 'All panels',          icon: <Columns3 size={14} /> },
    { mode: 'center-only',  label: 'Center only',         icon: <Maximize2 size={14} /> },
    { mode: 'left-center',  label: 'Left + Center',       icon: <PanelRightClose size={14} /> },
    { mode: 'center-right', label: 'Center + Right',      icon: <PanelLeftClose size={14} /> },
  ]

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => setOpen(o => !o)}
        title="Workspace layout"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreVertical size={15} />
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 6,
            // App-chrome tokens (not the page-theme `--pg-*` tokens, which
            // are scoped to the canvas and resolve to undefined at the
            // toolbar level — that left the menu fully transparent).
            background: 'var(--color-bg, #ffffff)',
            border: '1px solid var(--color-border, #e5e7eb)',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            padding: 4, minWidth: 200,
            // Sit above the property panel tabs/sticky headers below.
            zIndex: 1000,
          }}
        >
          <div style={{
            padding: '6px 8px 4px', fontSize: '0.8125rem',
            color: 'var(--color-text-secondary, #6b7280)',
            textTransform: 'uppercase', letterSpacing: '0.04em',
            fontWeight: 700,
          }}>
            Workspace layout
          </div>
          {items.map(({ mode, label, icon }) => {
            const active = value === mode
            return (
              <button
                key={mode}
                role="menuitemradio"
                aria-checked={active}
                onClick={() => { onChange(mode); setOpen(false) }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', padding: '7px 10px',
                  fontSize: '0.8825rem', textAlign: 'left',
                  background: active ? 'var(--color-accent-weak)' : 'transparent',
                  color: active ? 'var(--color-accent, #4c8dff)' : 'var(--color-text, #111827)',
                  border: 'none', borderRadius: 4, cursor: 'pointer',
                  lineHeight: 1.2,
                }}
              >
                <span style={{ display: 'inline-flex', flexShrink: 0 }}>{icon}</span>
                <span>{label}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

const resizerStyle: React.CSSProperties = {
  width: 6,
  cursor: 'col-resize',
  flexShrink: 0,
  position: 'relative',
  background: 'transparent',
}
const resizerLine: React.CSSProperties = {
  position: 'absolute', left: 2, top: 0, bottom: 0, width: 1,
  background: 'var(--pg-border)',
}
